import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { CheckCircle2, Eraser, FileUp, Loader2, Lock, PenLine, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.strokeStyle = "#F59E0B";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => {
    if (drawing.current) {
      drawing.current = false;
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };
  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange(null);
  };

  return (
    <div>
      <div className="relative border border-white/15 rounded-sm bg-[#07090D] overflow-hidden">
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          className="w-full h-32 touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        {!hasInk && (
          <span className="absolute inset-0 flex items-center justify-center text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-600 pointer-events-none">
            Or draw here instead
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={clear}
        data-testid="signature-clear-button"
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-500 hover:text-amber-400 transition-colors"
      >
        <Eraser className="w-3.5 h-3.5" /> Clear
      </button>
    </div>
  );
}

export default function SecureUploadAndSign({ leadId, onRestart }) {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [consent, setConsent] = useState(false);
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);

  const uploadFiles = async (list) => {
    const incoming = Array.from(list).slice(0, 5);
    if (!incoming.length) return;
    const form = new FormData();
    incoming.forEach((f) => form.append("files", f));
    const placeholders = incoming.map((f) => ({ name: f.name, size: f.size, status: "uploading" }));
    setFiles((prev) => [...prev, ...placeholders]);
    try {
      const { data } = await api.post(`/leads/${leadId}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFiles((prev) => [
        ...prev.filter((f) => !placeholders.some((p) => p.name === f.name && p.status === "uploading")),
        ...data.uploaded.map((u) => ({ name: u.original_filename, size: u.size, status: "done" })),
      ]);
      toast.success(`${data.uploaded.length} document(s) secured.`);
    } catch (e) {
      setFiles((prev) => prev.filter((f) => !placeholders.some((p) => p.name === f.name)));
      toast.error(formatApiError(e));
    }
  };

  const uploadedCount = files.filter((f) => f.status === "done").length;
  const canSubmit = uploadedCount >= 1 && signerName.trim().length > 2 && consent;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/leads/${leadId}/esign`, {
        signer_name: signerName.trim(),
        consent,
        signature_image: signature,
      });
      setComplete(true);
      confetti({
        particleCount: 140,
        spread: 75,
        origin: { y: 0.7 },
        colors: ["#F59E0B", "#F4F4F5", "#06B6D4"],
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (complete) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-10"
        data-testid="submission-complete"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        </div>
        <h3 className="font-display text-2xl font-medium tracking-tight text-zinc-100 mb-3">
          File received. The auction begins.
        </h3>
        <p className="text-sm leading-relaxed text-zinc-400 max-w-md mx-auto">
          Your statements and signed application are in underwriting. Expect competing term sheets
          from our lender network within <span className="text-amber-400">4 to 24 hours</span>.
        </p>
        <p className="mt-6 text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-600">
          Reference · {leadId.slice(0, 8).toUpperCase()}
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="secure-upload-section">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-emerald-400" />
        <span className="text-xs font-mono-d tracking-[0.2em] uppercase text-emerald-400">
          Secure intake
        </span>
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-medium tracking-tight text-zinc-100 mb-2">
        Statements &amp; e-sign.
      </h3>
      <p className="text-sm text-zinc-400 mb-8 leading-relaxed">
        Upload your last <span className="text-zinc-200 font-medium">3 months of business bank statements</span>{" "}
        and sign the application below. 256-bit encrypted, seen only by underwriting.
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current.click()}
        data-testid="statement-dropzone"
        className={`cursor-pointer border border-dashed rounded-sm p-8 sm:p-10 text-center transition-all duration-300 ${
          dragging ? "border-amber-500 bg-amber-500/[0.06]" : "border-white/15 bg-white/[0.02] hover:border-amber-500/40"
        }`}
      >
        <FileUp className={`w-8 h-8 mx-auto mb-4 transition-colors ${dragging ? "text-amber-400" : "text-zinc-500"}`} />
        <p className="text-sm text-zinc-300 font-medium">Drop bank statements here, or browse</p>
        <p className="text-xs text-zinc-500 mt-2 font-mono-d tracking-wide">PDF · PNG · JPG — up to 15MB each</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.webp"
          className="hidden"
          data-testid="statement-file-input"
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {files.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="uploaded-files-list">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center justify-between border border-white/10 rounded-sm px-4 py-2.5 bg-white/[0.02]">
              <span className="text-xs text-zinc-300 truncate mr-4">{f.name}</span>
              {f.status === "uploading" ? (
                <Loader2 className="w-4 h-4 text-amber-400 animate-spin shrink-0" />
              ) : (
                <span className="flex items-center gap-1.5 text-xs text-emerald-400 shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {(f.size / 1024).toFixed(0)} KB
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-10 border-t border-white/10 pt-8 space-y-6">
        <div>
          <label htmlFor="signer-name" className="block text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-500 mb-3">
            Full legal name
          </label>
          <input
            id="signer-name"
            type="text"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="As it appears on formation documents"
            data-testid="signer-name-input"
            className="w-full bg-[#07090D] border border-white/15 rounded-sm px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/60 transition-colors"
          />
          <div className="mt-3 border border-white/10 rounded-sm bg-[#07090D] px-5 py-4 min-h-[76px] flex items-center overflow-hidden" data-testid="signature-preview">
            {signerName.trim() ? (
              <span className="font-script text-4xl text-amber-400 leading-none select-none" data-testid="signature-preview-text">
                {signerName.trim()}
              </span>
            ) : (
              <span className="text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-700">
                Signature preview
              </span>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-500 mb-3">
            <PenLine className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Hand-drawn signature · optional
          </label>
          <SignaturePad onChange={setSignature} />
        </div>

        <label className="flex items-start gap-3 cursor-pointer group" data-testid="consent-checkbox-label">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="consent-checkbox"
            className="mt-0.5 w-4 h-4 rounded-sm accent-amber-500"
          />
          <span className="text-xs leading-relaxed text-zinc-400 group-hover:text-zinc-300 transition-colors">
            I authorize Forward Exspansion and its lender network to review my business bank statements for
            the purpose of underwriting a purchase of future receivables. I understand this is not a
            loan application and no hard credit inquiry will be made.
          </span>
        </label>

        <button
          onClick={submit}
          disabled={!canSubmit || submitting}
          data-testid="esign-submit-button"
          className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-30 disabled:hover:bg-amber-500 text-[#07090D] font-semibold text-sm px-6 py-4 rounded-sm transition-all duration-300 active:scale-[0.99]"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          {submitting ? "Securing your file…" : "Submit signed application"}
        </button>
        <p className="text-center text-xs text-zinc-600 flex items-center justify-center gap-1.5">
          <Lock className="w-3 h-3" /> 256-bit TLS · SOC 2 aligned storage
        </p>
      </div>
    </motion.div>
  );
}
